import React, { useRef } from "react";
import { Popup } from "@progress/kendo-react-popup";
import { icon, label } from "../../../../config";

/**
 * ProfilePicturePopUp Component
 * A popup menu component that provides options to change or remove a user's profile picture.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.RefObject} props.anchor - Reference to the element that the popup should be anchored to
 * @param {boolean} props.show - Controls the visibility of the popup
 * @param {Function} props.onClose - Callback function called when the popup should close
 * @param {Function} props.onChangePicture - Callback function called when user wants to change their profile picture
 * @param {Function} props.onRemovePicture - Callback function called when user wants to remove their profile picture
 * @param {Function} props.handleFileInput - Callback function to handle file input for new profile picture
 * @returns {JSX.Element} A popup menu with options to change or remove profile picture
 * @author Sejal
 */
const ProfilePicturePopUp = ({ anchor, show, onClose, onChangePicture, onRemovePicture }) => {
  const popupRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const onOpenPopup = () => {
    if (popupRef.current) popupRef.current.focus();
  };

  const onFocusPopup = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
  };

  const onBlurPopup = () => {
    blurTimeoutRef.current = setTimeout((e) => {
      onClose(e);
    }, 100);
  };

  return (
    <Popup anchor={anchor?.current} show={show} onOpen={onOpenPopup} className={"dt-popup profile-settings-container"}>
      <div tabIndex={0} ref={popupRef} onBlur={onBlurPopup} onFocus={onFocusPopup} className='d-flex flex-column align-items-start ml-2'>
        <div
          className='d-flex align-items-center w-100 py-2 cursor-pointer'
          onClick={(e) => {
            e.stopPropagation();
            onChangePicture();
          }}>
          <span className='mr-2'>{icon.EDIT_ICON}</span>
          <span>{label.CHANGE_PROFILE_PICTURE}</span>
        </div>

        <div
          className='d-flex align-items-center w-100 py-2 cursor-pointer'
          onClick={(e) => {
            e.stopPropagation();
            onRemovePicture(e);
            onClose(e);
          }}>
          <span className='mr-2'>{icon.EMAIL_TRASH}</span>
          <span>{label.REMOVE_PHOTO}</span>
        </div>
      </div>
    </Popup>
  );
};

export default ProfilePicturePopUp;
